// useAppointmentReminders — polls every 30s, fires:
//   1. Morning brief (once per day on first mount): toast listing today's scheduled appointments.
//   2. 15-min pre-call reminder per appointment (once per appointment instance).
//
// Persisted state (localStorage):
//   kf_reminders_morning_<YYYY-MM-DD> = '1'
//   kf_reminders_fired_<lead_id>_<isoMinute>  = '1'
//
// Cheap, robust to reload, no server round-trip beyond the existing /api/leads polling.
//
// Plus: exposes a global `window.kfTestReminder()` for quick manual verification.

import { useEffect, useRef } from 'react';
import { useLeads } from '../lib/leadsStore';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const TICK_MS = 15_000;       // check every 15s (was 30s — tighter window)
const WINDOW_MS = 16 * 60_000; // window: 0..16 min before
const PASS_GRACE_MS = 60_000;  // ignore appointments more than 60s in the past

export default function useAppointmentReminders() {
  const leads = useLeads();
  const navigate = useNavigate();
  const morningFiredRef = useRef(false);

  useEffect(() => {
    // 1. Morning brief — runs whenever a new appointment for today appears
    //    that wasn't included in a previous brief. Once-per-set-of-todays-appointments,
    //    keyed by today's date + the count + ids hash so that adding/removing causes a re-fire.
    if (!morningFiredRef.current && leads && leads.length) {
      const todays = leads
        .filter((l) => l.appointmentAt && isSameDay(new Date(l.appointmentAt), new Date()))
        .sort((a, b) => new Date(a.appointmentAt) - new Date(b.appointmentAt));
      if (todays.length) {
        const fingerprint = todays.map((l) => l.id).join(',');
        const key = `kf_reminders_morning_${ymd(new Date())}_${fingerprint}`;
        if (!localStorage.getItem(key)) {
          const sample = todays.slice(0, 3).map((l) => `${l.name} (${fmtTime(l.appointmentAt)})`).join(' · ');
          const more = todays.length > 3 ? ` · +${todays.length - 3} more` : '';
          toast(`Today's calls — ${todays.length}`, {
            description: sample + more,
            duration: 9000,
            action: {
              label: 'View',
              onClick: () => navigate('/leads'),
            },
          });
          localStorage.setItem(key, '1');
        }
      }
      morningFiredRef.current = true;
    }
  }, [leads, navigate]);

  useEffect(() => {
    if (!leads || !leads.length) return;
    let stopped = false;

    const tick = () => {
      if (stopped) return;
      const now = Date.now();
      for (const l of leads) {
        if (!l.appointmentAt) continue;
        const t = new Date(l.appointmentAt).getTime();
        const diff = t - now;
        // Within the 0-16 minute pre-call window (and not already past + grace)
        if (diff > -PASS_GRACE_MS && diff <= WINDOW_MS) {
          // Key per appointment instance (tied to the specific minute, so a reschedule re-arms)
          const key = `kf_reminders_fired_${l.id}_${l.appointmentAt}`;
          if (!localStorage.getItem(key)) {
            const minsToCall = Math.max(0, Math.round(diff / 60_000));
            const headline = minsToCall <= 0 ? `Now: call ${l.name}` : `In ${minsToCall} min: call ${l.name}`;
            toast(headline, {
              description: `${l.phone || 'No phone on file'} · ${l.city || ''}`,
              duration: 12000,
              action: {
                label: 'Open',
                onClick: () => navigate(`/leads/${l.id}`),
              },
            });
            // Also fire a desktop notification if granted
            if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
              try {
                new Notification(headline, {
                  body: `${l.phone || 'No phone'} · ${l.nextAction || ''}`,
                  silent: false,
                });
              } catch {}
            }
            localStorage.setItem(key, '1');
          }
        }
      }
    };

    // Ask for desktop notification permission lazily on first run
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      try { Notification.requestPermission().catch(() => {}); } catch {}
    }

    // Expose a manual test trigger on window for debugging.
    // Usage: kfTestReminder('l1') — fires a sample reminder for that lead.
    window.kfTestReminder = (leadId) => {
      const target = leads.find((l) => l.id === leadId) || leads[0];
      if (!target) {
        toast.error('No leads available to test with');
        return;
      }
      toast(`In 15 min: call ${target.name}`, {
        description: `${target.phone || 'No phone on file'} · ${target.city || ''}`,
        duration: 12000,
        action: {
          label: 'Open',
          onClick: () => navigate(`/leads/${target.id}`),
        },
      });
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        try {
          new Notification(`In 15 min: call ${target.name}`, {
            body: `${target.phone || 'No phone'} · ${target.nextAction || ''}`,
            silent: false,
          });
        } catch {}
      }
    };

    tick();
    const id = setInterval(tick, TICK_MS);
    return () => { stopped = true; clearInterval(id); };
  }, [leads, navigate]);
}

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function isSameDay(a, b) { return a.toDateString() === b.toDateString(); }
function fmtTime(iso) {
  return new Date(iso).toLocaleTimeString('en-US', { timeZone: 'America/Phoenix', hour: 'numeric', minute: '2-digit' });
}
